import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, Button, Paper, Grid } from '@mui/material';
import { useService } from 'contexts/ServiceContextProvider';
import StorageIcon from '@mui/icons-material/Storage';
import GroupIcon from '@mui/icons-material/Group';
import EuroIcon from '@mui/icons-material/Euro';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTranslation } from "react-i18next";
import "../../css/components/Modals/ResubscriptionModal.css";
import { ConvertToMyTimeZone, formatDateToString } from 'components/Custom/TimezoneConvertion.jsx';

import { useSnackbar } from 'contexts/SnackbarContextProvider';

import { DateTime } from 'luxon';

const ResubscriptionModal = ({ open, onClose, subscription, plan, loggedUserID, workspace_id }) => {
    const { t } = useTranslation();
    const { getUserTimeZone, ResubscribePlan } = useService();
    const { showSnackbar } = useSnackbar();

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        const fetchUserTimezone = async () => {
            try {
                const myTime = await getUserTimeZone(loggedUserID);
                setMyUserTimezone(myTime.timezone)
            } catch (error) {
            }
        };

        fetchUserTimezone();
    }, [loggedUserID]);

    // Parse dates
    const statusUpdateTime = subscription.status_update_time;
    const createdTime = subscription.create_time;
    const startTime = subscription.start_time;
    const [myUserTimezone, setMyUserTimezone] = useState(null);

    // Calculate the end date (30 days after the start time for monthly subscriptions)
    const endDate = DateTime.fromISO(startTime).plus({ months: 1 }).toISO();

    const handleResubscribe = async () => {
        const response = await ResubscribePlan(workspace_id, subscription.id);
        if (response.message === "Subscription successfully reactivated.") {
            showSnackbar(t("workspace_settings.resub_success"), 'success');
            setTimeout(() => {
                window.location.reload();
            }, 1500);
        } else {
            showSnackbar(t("workspace_settings.resub_fail"), 'error');
            setTimeout(() => {
                window.location.reload();
            }, 1500); 
        }
        

    };

    return (
        <Modal open={open} onClose={onClose} className="resub-modal">
            <Box className="resub-modal-box">
                <Typography id="resubscribe-modal-title" variant="h5" gutterBottom>
                    {t('workspace_settings.resubscribe_title')}
                </Typography>
                <Typography id="resubscribe-modal-description" variant="body1" gutterBottom>
                    {t('workspace_settings.resubscribe_message', { planName: plan.main_plan.name })}
                </Typography>

                {/* Suspension, Creation, and Duration Info */}
                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" gutterBottom>
                        <b>{t("workspace_settings.subscription_status")}: </b>{subscription.status}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        <b>{t("workspace_settings.status_update_time")}: </b>
                        {statusUpdateTime && myUserTimezone
                            ? formatDateToString(ConvertToMyTimeZone(statusUpdateTime, myUserTimezone))
                            : t("workspace_settings.timezone_loading")}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        <b>{t("workspace_settings.created_time")}: </b>
                        {createdTime && myUserTimezone
                            ? formatDateToString(ConvertToMyTimeZone(createdTime, myUserTimezone))
                            : t("workspace_settings.timezone_loading")}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        <b>{t("workspace_settings.start_time")}: </b>
                        {startTime && myUserTimezone
                            ? formatDateToString(ConvertToMyTimeZone(startTime, myUserTimezone))
                            : t("workspace_settings.timezone_loading")}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        <b>{t("workspace_settings.end_time")}: </b>
                        {endDate && myUserTimezone
                            ? formatDateToString(ConvertToMyTimeZone(endDate, myUserTimezone))
                            : t("workspace_settings.timezone_loading")}
                    </Typography>
                </Box>


                <Box className="resub-summary" sx={{ flexDirection: 'column', rowGap: 12, mt: 2 }}>
                    <Paper elevation={2} className="resub-summary-paper resub-summary-paper-group">
                        <GroupIcon className="resub-summary-icon-group" />
                        <Typography variant="body1" className="resub-summary-text">
                            {t("workspace_settings.plan_members")}: <b>{plan.amount_members}</b>
                        </Typography>
                    </Paper>
                    <Paper elevation={2} className="resub-summary-paper resub-summary-paper-storage">
                        <StorageIcon className="resub-summary-icon-storage" />
                        <Typography variant="body1" className="resub-summary-text">
                            {t("workspace_settings.plan_storage")}: <b>{Number(plan.storage).toFixed(2)} GB</b>
                        </Typography>
                    </Paper>
                    <Paper elevation={2} className="resub-summary-paper resub-summary-paper-price">
                        <EuroIcon className="resub-summary-icon-price" />
                        <Typography variant="body1" className="resub-summary-text">
                            {t("workspace_settings.plan_price")}: <b>{plan.price}€</b>
                        </Typography>
                    </Paper>
                </Box>

                {plan.features?.length >= 1 && (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 2 }}>
                        <Typography variant="h6">{t("workspace_settings.plan_features_title")}</Typography>
                        <ul>
                            {plan.features.map((feature) => (
                                <li key={feature.id}>
                                    <Typography sx={{ color: "#0576c7" }} variant="body1">
                                        {feature.name}
                                    </Typography>
                                    <Typography variant="subtitle2">{feature.description}</Typography>
                                </li>
                            ))}
                        </ul>
                    </Box>
                )}

                <Grid container spacing={2} sx={{ mt: 3 }}>
                    <Grid item xs={6}>
                        <Button
                            onClick={onClose}
                            variant="contained"
                            color="error"
                            fullWidth
                            startIcon={<CancelIcon />}
                            className="resub-modal-button cancel-button"
                        >
                            {t('global.close')}
                        </Button>
                    </Grid>
                    <Grid item xs={6}>
                        <Button
                            onClick={handleResubscribe}
                            variant="contained"
                            fullWidth
                            startIcon={<CheckCircleIcon />}
                            className="resub-modal-button"
                            sx={{
                                bgcolor: '#0576c7',
                                '&:hover': { bgcolor: '#045a9c' },
                            }}
                        >
                            {t('workspace_settings.resubscribe')}
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </Modal>
    );
};

export default ResubscriptionModal;
